import { useState, useEffect } from 'react'
import ReauthModal from './ReauthModal'
import { reauthService } from '../utils/reauthService'
import { apiClient } from '../api/client'
import { TOKEN_VALIDATION_ENDPOINT } from '../api/config'

export function ReauthProvider({ children }: { children: React.ReactNode }) {
  const [isReauthModalOpen, setIsReauthModalOpen] = useState(false)
  const [reauthError, setReauthError] = useState<string>()

  useEffect(() => {
    // Register modal open/close listener
    reauthService.setModalOpenListener((isOpen) => {
      setIsReauthModalOpen(isOpen)
      if (!isOpen) {
        setReauthError(undefined)
      }
    })

    // Cleanup
    return () => {
      reauthService.setModalOpenListener(() => {})
    }
  }, [])

  const handleReauth = async (token: string) => {
    try {
      // Test the token with a lightweight admin endpoint
      const testConfig = {
        method: 'GET',
        url: TOKEN_VALIDATION_ENDPOINT,
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Auth-Type': 'gcp'
        }
      }

      await apiClient.request(testConfig)

      // Token is valid - proceed with reauth
      await reauthService.handleReauthSuccess(token, apiClient)
      setReauthError(undefined)
    } catch (error: any) {
      // Token is invalid
      setReauthError(
        error.response?.status === 401
          ? 'Invalid token. Please check and try again.'
          : 'Failed to authenticate. Please try again.'
      )
      throw error
    }
  }

  const handleCancel = () => {
    reauthService.handleReauthCancel()
    setReauthError(undefined)
  }

  return (
    <>
      {children}
      <ReauthModal
        isOpen={isReauthModalOpen}
        onReauth={handleReauth}
        onCancel={handleCancel}
        errorMessage={reauthError}
      />
    </>
  )
}
